import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { lt } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import {
  AUTOMAPPING_STATUS,
  type AutomappingStatus,
} from 'lumen/utils/central-mapping-constants';

import styles from './trigger-central-mapping.module.css';

export const TRIGGER_CENTRAL_MAPPING_VARIANTS = {
  DEFAULT: 'default',
  SUBDUED: 'subdued',
} as const;

export type TriggerCentralMappingVariant =
  (typeof TRIGGER_CENTRAL_MAPPING_VARIANTS)[keyof typeof TRIGGER_CENTRAL_MAPPING_VARIANTS];

interface SettingsCentralMappingDropdownsTriggersTriggerCentralMappingSignature {
  Args: {
    breadcrumbs: string[];
    isMixed?: boolean;
    isReadOnly?: boolean;
    isSelected?: boolean;
    mappingStatus: AutomappingStatus;
    select: {
      isOpen: boolean;
    };
    variant: TriggerCentralMappingVariant;
  };
}

export default class SettingsCentralMappingDropdownsTriggersTriggerCentralMapping extends Component<SettingsCentralMappingDropdownsTriggersTriggerCentralMappingSignature> {
  get breadcrumbIconColor(): 'default' | 'intelligence' {
    const { mappingStatus } = this.args;

    if (mappingStatus === AUTOMAPPING_STATUS.AUTO_MAPPED) {
      return 'intelligence';
    }

    return 'default';
  }

  get classesForContent(): string[] {
    const { mappingStatus, isMixed, isReadOnly, isSelected, variant } =
      this.args;

    const classes: string[] = ['content'];

    if (variant === 'subdued') {
      classes.push('content-subdued');
    }

    if (isMixed || isSelected) {
      if (isMixed) {
        classes.push('content-mixed');
      }

      if (isSelected) {
        classes.push('content-selected');
      }
    } else {
      switch (mappingStatus) {
        case AUTOMAPPING_STATUS.AUTO_MAPPED: {
          classes.push('content-auto-mapped');
          break;
        }

        case AUTOMAPPING_STATUS.AUTO_MAPPED_FROZEN:
        case AUTOMAPPING_STATUS.USER_MAPPED: {
          break;
        }

        default: {
          classes.push('content-unmapped');
        }
      }
    }

    if (isReadOnly) {
      classes.push('content-read-only');
    }

    return classes;
  }

  get lastBreadcrumbIndex(): number {
    return this.args.breadcrumbs.length - 1;
  }

  get triggerIconColor(): 'default' | 'on-intelligence' {
    const { mappingStatus, isMixed } = this.args;

    if (isMixed) {
      return 'default';
    }

    if (mappingStatus === AUTOMAPPING_STATUS.AUTO_MAPPED) {
      return 'on-intelligence';
    }

    return 'default';
  }

  <template>
    <div
      class={{concat
        (local styles this.classesForContent)
        " "
        "text-200--medium flex flex-row items-center gap-100 overflow-hidden py-50 pr-100 pl-200"
      }}
    >
      {{#if @breadcrumbs.length}}
        <div class="flex flex-row gap-100 overflow-hidden">
          {{#each @breadcrumbs as |breadcrumb index|}}
            <div
              class={{concat
                (local
                  styles
                  "breadcrumb"
                  (if (lt index this.lastBreadcrumbIndex) "breadcrumb-subdued")
                )
                " "
                "overflow-hidden text-ellipsis whitespace-nowrap"
              }}
            >
              {{breadcrumb}}
            </div>

            {{#if (lt index this.lastBreadcrumbIndex)}}
              <PrismIcon
                @color={{this.breadcrumbIconColor}}
                @name="chevron-right"
                @size="small"
              />
            {{/if}}
          {{/each}}
        </div>
      {{else if @isMixed}}
        {{t "settings.central_mapping.mapping.mixed"}}
      {{else}}
        {{t "general.unmapped"}}
      {{/if}}

      {{#unless @isReadOnly}}
        <PrismIcon
          @color={{this.triggerIconColor}}
          @name={{if @select.isOpen "chevron-up" "chevron-down"}}
        />
      {{/unless}}
    </div>
  </template>
}
